/**
 * @fileoverview Deactivate User dialog.
 * @description Deactivates a user through POST /users/{userId}/deactivate. The dialog reads the
 * user's details first, because the command carries their ETag as If-Match: if someone else
 * changed the user meanwhile (412 / 3022), the details are re-read and the form starts again,
 * keeping the message so the admin knows to review and confirm again. The admin picks a reason from
 * the configuration's list (GET /users/configuration) and gives a note for OTHER. The dialog says
 * what deactivating does: the user is signed out everywhere, anyone reporting to them moves up to
 * the nearest eligible manager (said only when someone does report to them), and a pending
 * invitation is revoked. Confirm stays disabled until the direct-reports read has settled, so the
 * admin is never asked to confirm without being told whether reports will move; if that read fails
 * the dialog says so and Deactivate stays disabled. A user who owns a prospect can't be
 * deactivated (409 / 3029), and a "recent sign-in required" refusal (403 / 2009) offers Sign in
 * again. The page mounts this only while a user is being deactivated, so every open starts clean
 * (see the modal-lifecycle rule). The data and the command are in useDeactivateUserDialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import DeactivateUserForm from '@/features/users/components/DeactivateUserForm'
import UserRequestError from '@/features/users/components/UserRequestError'
import {
  DEACTIVATE_FORM_ID,
  DEACTIVATE_TEST_ID,
  DEFAULT_REASON_NOTE_MAX_LENGTH,
} from '@/features/users/constants/lifecycle.constants'
import { useDeactivateUserDialog } from '@/features/users/hooks/useDeactivateUserDialog'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import Modal from '@/shared/components/molecules/Modal'

import type { UserDetail } from '@/features/users/types/users.types'

interface DeactivateUserModalProps {
  userId: string
  onClose: () => void
  onDeactivated: (user: UserDetail) => void
}

/**
 * @component
 * @param {{ dialog: ReturnType<typeof useDeactivateUserDialog> }} props - The dialog's data and
 * command.
 * @param {ReturnType<typeof useDeactivateUserDialog>} props.dialog - The dialog's data and command.
 */
function DeactivateUserBody({ dialog }: { dialog: ReturnType<typeof useDeactivateUserDialog> }) {
  const { t } = useTranslation()
  const { detail, configuration } = dialog

  return (
    <>
      {dialog.loading && (
        <p className="text-[13px] text-slate" data-testid={`${DEACTIVATE_TEST_ID}-loading`}>
          {t('sales.userManagement.viewUser.loading')}
        </p>
      )}

      {dialog.loadError && (
        <UserRequestError error={dialog.loadError} testIdPrefix={DEACTIVATE_TEST_ID} />
      )}

      {detail && configuration && (
        <div className="flex flex-col gap-4">
          {!dialog.allowed && (
            <p
              className="text-[13px] text-danger"
              data-testid={`${DEACTIVATE_TEST_ID}-not-allowed`}
            >
              {t('sales.userManagement.lifecycle.notAvailable')}
            </p>
          )}
          {dialog.reportsLoading && !dialog.reportsError && (
            <p
              className="text-[13px] text-slate"
              data-testid={`${DEACTIVATE_TEST_ID}-reports-loading`}
            >
              {t('sales.userManagement.deactivateUser.checkingReports', {
                name: detail.user.displayName,
              })}
            </p>
          )}
          {dialog.reportsError && (
            <div role="alert" data-testid={`${DEACTIVATE_TEST_ID}-reports-error`}>
              <p className="text-[13px] text-danger">
                {t('sales.userManagement.deactivateUser.reportsLoadFailed', {
                  name: detail.user.displayName,
                })}
              </p>
              <UserRequestError
                error={dialog.reportsError}
                testIdPrefix={`${DEACTIVATE_TEST_ID}-reports`}
              />
              <button
                type="button"
                onClick={dialog.retryReports}
                data-testid={`${DEACTIVATE_TEST_ID}-reports-retry-button`}
                className="mt-1 cursor-pointer text-[13px] font-semibold text-primary underline"
              >
                {t('sales.userManagement.deactivateUser.reportsRetry')}
              </button>
            </div>
          )}
          {/* Not keyed by version: a re-read after a conflict must not wipe the typed reason. */}
          <DeactivateUserForm
            user={detail.user}
            hasReports={dialog.hasReports ?? false}
            reasons={configuration.lifecycleReasons.deactivation}
            noteMaxLength={
              configuration.limits.reasonNoteMaxLength || DEFAULT_REASON_NOTE_MAX_LENGTH
            }
            onSubmit={dialog.submit}
          />
          {dialog.submitError && (
            <UserRequestError error={dialog.submitError} testIdPrefix={DEACTIVATE_TEST_ID} />
          )}
        </div>
      )}
    </>
  )
}

/**
 * @component
 * @param {DeactivateUserModalProps} props - The user to deactivate, and the dialog's callbacks.
 * @param {string} props.userId - The user to deactivate.
 * @param {() => void} props.onClose - Dismisses the dialog without deactivating.
 * @param {(user: UserDetail) => void} props.onDeactivated - Called with the deactivated user after
 * a successful deactivation, before the dialog closes.
 */
function DeactivateUserModal({ userId, onClose, onDeactivated }: DeactivateUserModalProps) {
  const { t } = useTranslation()
  const dialog = useDeactivateUserDialog({ userId, onClose, onDeactivated })
  const { detail } = dialog

  return (
    <Modal
      data-testid={`${DEACTIVATE_TEST_ID}-modal`}
      open
      onClose={dialog.close}
      title={t('sales.userManagement.deactivateUser.title')}
      subtitle={
        detail
          ? t('sales.userManagement.deactivateUser.subtitle', {
              name: detail.user.displayName,
              role: detail.user.primaryRole?.name ?? '',
            })
          : ''
      }
      footer={
        <>
          <ModalFooterButton
            type="button"
            onClick={dialog.close}
            disabled={dialog.isSubmitting}
            data-testid={`${DEACTIVATE_TEST_ID}-cancel-button`}
          >
            {t('sales.userManagement.editUserForm.cancel')}
          </ModalFooterButton>
          <ModalFooterButton
            type="submit"
            variant="danger"
            form={DEACTIVATE_FORM_ID}
            data-testid={`${DEACTIVATE_TEST_ID}-submit-button`}
            isLoading={dialog.isSubmitting}
            disabled={!dialog.canSubmit}
          >
            {t('sales.userManagement.deactivate')}
          </ModalFooterButton>
        </>
      }
    >
      <DeactivateUserBody dialog={dialog} />
    </Modal>
  )
}

export default DeactivateUserModal
